import {
    ArrowBottomIcon,
    Box,
    DaTable,
    DaTableBody,
    DaTableCell,
    DaTableHead,
    DaTableHeadCell,
    DaTableRow,
    Text,
    ThemeDefault,
} from '@paygreen/paygreen-ui';
import React, { useEffect, useMemo, useState } from 'react';
import { withTranslation } from 'react-i18next';
import {
    useExpanded,
    useFilters,
    usePagination,
    useRowSelect,
    useSortBy,
    useTable,
} from 'react-table';
import {
    CarbonDataDisplay,
    PaginationTable,
    StatusDisplayTable,
    StatusFilterTable,
} from '../../../components';
import { statusContributions } from '../../../utils/constants';
import { IsBigDesktop, IsMobile, timestampToDate } from '../../../utils/tools';
import { statusOptions } from './statusOptions';

const ContributionsTable = ({
    t,
    data,
    fetchData,
    loading,
    pageCount: controlledPageCount,
    totalItems,
}) => {
    const [statusFilter, setStatusFilter] = useState('');
    const [activePageReset, setActivePageReset] = useState(false);

    // needed to guarantee vertical alignment and layout on mobile for status cell in table
    const statusCellboxStyle = {
        justifyContent: IsBigDesktop() ? null : 'flex-start',
        minWidth: IsMobile() ? `${ThemeDefault.blockHeader.sm}` : null,
        display: !IsBigDesktop() ? 'flex' : null,
    };

    const columns = useMemo(
        () => [
            {
                Header: t(
                    'contributions.allMyContributions.table.contribution',
                ),
                accessor: 'created_at',
            },
            {
                Header: '',
                accessor: 'status',
                Filter: ({ filter, ...rest }) => (
                    <StatusFilterTable
                        onChange={setStatusFilter}
                        setActivePageReset={setActivePageReset}
                        statusConstants={statusContributions}
                        i18TextBase="contributions.allMyContributions.table.status"
                        {...rest}
                    />
                ),
                filter: 'includes',
            },
            {
                Header: t('contributions.allMyContributions.table.projectName'),
                accessor: 'project.name',
            },
            {
                Header: t('contributions.allMyContributions.table.carbonPrice'),
                accessor: 'project.carbonPrice',
            },
            {
                Header: t('contributions.allMyContributions.table.amountPaid'),
                accessor: 'price',
            },
        ],
        [t, setActivePageReset],
    );

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        pageCount,
        setPageSize,
        gotoPage,
        state: { pageIndex, pageSize },
    } = useTable(
        {
            columns,
            data,
            initialState: { pageIndex: 0, pageSize: 25 },
            manualPagination: true,
            pageCount: controlledPageCount,
            manualSortBy: false,
            autoResetPage: false,
            // we ponctually override table internal state to resetPage index with component SelectColumnFilter when needed
            useControlledState: (state) => {
                if (activePageReset) {
                    return { ...state, pageIndex: 0 };
                } else {
                    return state;
                }
            },
        },
        useFilters,
        useSortBy,
        useExpanded,
        usePagination,
        useRowSelect,
    );

    useEffect(() => {
        fetchData(pageSize, pageIndex + 1, statusFilter);
    }, [fetchData, pageIndex, pageSize, statusFilter]);

    return (
        <Box marginBottom="md">
            <DaTable
                {...getTableProps()}
                blockWidth="none"
                isLoading={loading}
                loadingRowNumber={10}
                loadingMainCellNumber={2}
            >
                {headerGroups.map((headerGroup) => (
                    <DaTableHead
                        blockTitle={t('user.list.table.title')}
                        {...headerGroup.getHeaderGroupProps()}
                    >
                        {headerGroup.headers.map((column, index) => (
                            <DaTableHeadCell
                                key={index}
                                label={column.render('Header')}
                                sortIcon={
                                    index === 0 ? (
                                        <ArrowBottomIcon
                                            {...column.getSortByToggleProps()}
                                            isActive={column.isSorted}
                                            rotateSize={
                                                column.isSorted &&
                                                column.isSortedDesc
                                                    ? 'd0'
                                                    : 'd180'
                                            }
                                            title={
                                                column.isSortedDesc
                                                    ? t(
                                                          'user.billing.table.cancelSort',
                                                      )
                                                    : t(
                                                          'user.billing.table.sort',
                                                      ) +
                                                      (column.isSorted
                                                          ? 'DESC'
                                                          : 'ASC') +
                                                      t(
                                                          'user.billing.table.sample',
                                                      )
                                            }
                                        />
                                    ) : null
                                }
                            >
                                {index === 1 ? column.render('Filter') : null}
                            </DaTableHeadCell>
                        ))}
                    </DaTableHead>
                ))}

                <DaTableBody {...getTableBodyProps()}>
                    {!loading && data.length ? (
                        page.map((row) => {
                            prepareRow(row);

                            return (
                                <DaTableRow {...row.getRowProps()} key={row.id}>
                                    <DaTableCell
                                        label={t(
                                            'contributions.allMyContributions.table.contribution',
                                        )}
                                    >
                                        {timestampToDate(row.values.created_at)}
                                    </DaTableCell>

                                    <DaTableCell
                                        style={{ ...statusCellboxStyle }}
                                    >
                                        <StatusDisplayTable
                                            status={row.values.status}
                                            statusOptions={statusOptions}
                                            i18TextBase="contributions.allMyContributions.table.status"
                                        />
                                    </DaTableCell>

                                    <DaTableCell
                                        label={t(
                                            'contributions.allMyContributions.table.projectName',
                                        )}
                                        isMain={false}
                                    >
                                        {/* Since future multiproject mode will change completely projects API requests, we keep static values for only one project */}
                                        {row.values['project.name']}
                                    </DaTableCell>

                                    <DaTableCell
                                        isMain={false}
                                        label={t(
                                            'contributions.allMyContributions.table.carbonPrice',
                                        )}
                                    >
                                        {/* Since future multiproject mode will change completely projects API requests, we keep static values for only one carbon price */}
                                        <CarbonDataDisplay
                                            hasConversionMode={false}
                                            value={
                                                row.values[
                                                    'project.carbonPrice'
                                                ]
                                            }
                                            mode="price"
                                            unit={t(
                                                'contributions.allMyContributions.table.unit',
                                            )}
                                            customGrid="1fr auto"
                                        />
                                    </DaTableCell>

                                    <DaTableCell
                                        label={t(
                                            'contributions.allMyContributions.table.amountPaid',
                                        )}
                                    >
                                        <CarbonDataDisplay
                                            value={
                                                row.values.status === 'ERROR'
                                                    ? 0
                                                    : row.values.price
                                            }
                                            mode="price"
                                            hasZeroDisplayed={true}
                                        />
                                    </DaTableCell>
                                </DaTableRow>
                            );
                        })
                    ) : (
                        <Text
                            align="center"
                            marginTop="xl"
                            marginBottom="xl"
                            textSize="sm"
                        >
                            {t('noDataFound')}
                        </Text>
                    )}
                </DaTableBody>
            </DaTable>

            {totalItems > 25 ? (
                <PaginationTable
                    setPageSize={setPageSize}
                    pageCount={pageCount || 1}
                    pageIndex={pageIndex}
                    gotoPage={gotoPage}
                />
            ) : null}
        </Box>
    );
};

export default withTranslation('common')(ContributionsTable);
